import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { SimulatorDialog } from "@/components/coaching/SimulatorDialog";
import { api } from "@/lib/api";
import { ProvenanceBadge } from "@/components/ProvenanceBadge";
import { Activity, Clock, FlaskConical } from "lucide-react";
import { fmtDate } from "@/lib/format";
import Tip from "@/components/Tip";

const KEYS = ["readiness", "sharpness", "effort", "rest"];
const KEY_LABEL = { readiness: "Readiness", sharpness: "Sharpness", effort: "Effort+", rest: "Rest+" };

export default function TodayBar({ athleteId }) {
  const [snap, setSnap] = useState(null), [sim, setSim] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    if (!athleteId) return;
    let active = true;
    const fetchSnap = () => api.get(`/athletes/${athleteId}/snapshot`).then((r) => { if (active) setSnap(r.data); }).catch(() => { if (active) setSnap(null); });
    fetchSnap();
    const onSynced = (e) => { if (!e?.detail?.athleteId || e.detail.athleteId === athleteId) fetchSnap(); };
    window.addEventListener("trifit-synced", onSynced);
    return () => { active = false; window.removeEventListener("trifit-synced", onSynced); };
  }, [athleteId]);
  if (!snap) return null;
  const pending = snap.readiness == null && snap.rest == null;
  const riskColor = snap.injury_risk >= 66 ? "text-traffic-red" : snap.injury_risk >= 33 ? "text-foreground" : "text-traffic-green";
  const prov = snap.provenance || {};
  const isToday = snap.as_of && snap.as_of === snap.date;
  const stamp = isToday ? "Today" : snap.as_of ? `As of ${fmtDate(snap.as_of)}` : "Latest";
  return (
    <div className="border-b border-border bg-surface px-4 sm:px-8 py-3 flex items-center gap-4 sm:gap-6 flex-wrap" data-testid="today-bar">
      <span className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
        <Activity className="w-4 h-4" /> {snap.name}
      </span>
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="today-asof" title={isToday ? "Data is current" : "Latest data available · not today"}>
        <Clock className="w-3.5 h-3.5" /> {stamp}
      </span>
      {pending && (
        <span className="text-xs text-muted-foreground" data-testid="today-pending-setup">
          Account Created - Pending Setup · scores and injury risk appear once initial data arrives.
        </span>
      )}
      {!pending && KEYS.map((k) => (
        <div key={k} className="flex items-center gap-1.5" data-testid={`today-${k}`}>
          <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">{KEY_LABEL[k] || k}</span>
          <span className="font-heading text-lg font-black tabular-nums">{snap[k]}</span>
          <ProvenanceBadge source={prov[k]} />
        </div>
      ))}
      {!pending && <div className="flex items-center gap-1.5" data-testid="today-injury">
        <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground flex items-center gap-1">
          Injury Risk
          <Tip label="Injury Risk" text="The athlete's current model-estimated injury risk. In the Athlete Overview calendar the same model is shown as 'Predicted Injury Risk' because it is projected forward for a planned day." />
        </span>
        <span className={`font-heading text-lg font-black tabular-nums ${riskColor}`}>{snap.injury_risk}%</span>
        <ProvenanceBadge source={prov.injury_risk} />
      </div>}
      {user?.role !== "athlete" && <div className="ml-auto flex items-center gap-4">
        <button onClick={() => setSim(true)} data-testid="today-sim-link" className="text-sm flex items-center gap-1.5 border border-border px-3 py-1.5 hover:bg-background transition-colors">
          <FlaskConical className="w-4 h-4" /> Open Simulator
        </button>
        <SimulatorDialog open={sim} onClose={() => setSim(false)} athleteId={athleteId} />
      </div>}
    </div>
  );
}
